import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';

export default function SWAIDocPage() {

  const [scanFile, setScanFile] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [scanning, setScanning] = useState(false);

  const [scanResult, setScanResult] = useState<any>(null);

  const [aiData, setAiData] = useState<any>(null);

  const compressImage = (
    file: File
  ): Promise<File> => {

    return new Promise((resolve, reject) => {

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = (event) => {

        const img = new Image();

        img.src =
          event.target?.result as string;

        img.onload = () => {

          const canvas =
            document.createElement('canvas');

          const ctx =
            canvas.getContext('2d');

          if (!ctx) {

            reject('Canvas failed');

            return;
          }

          // KEEP ORIGINAL SIZE IF SMALL

          let width = img.width;
          let height = img.height;

          // RESIZE ONLY IF TOO LARGE

          const maxWidth = 1200;

          if (width > maxWidth) {

            const ratio =
              maxWidth / width;

            width = maxWidth;

            height = height * ratio;
          }

          canvas.width = width;
          canvas.height = height;

          // WHITE BACKGROUND FIX
          // prevents OCRExitCode 99

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);

          ctx.drawImage(
            img,
            0,
            0,
            width,
            height
          );

          canvas.toBlob(
            (blob) => {

              if (!blob) {

                reject('Compression failed');

                return;
              }

              const compressedFile =
                new File(
                  [blob],
                  'compressed.jpg',
                  {
                    type: 'image/jpeg',
                  }
                );

              resolve(compressedFile);

            },
            'image/jpeg',
            0.8
          );
        };

        img.onerror = () => {

          reject('Image load failed');
        };
      };

      reader.onerror = () => {

        reject('File read failed');
      };
    });
  };

  const handleScan = async () => {

    if (!selectedFile) return;

    setScanning(true);

    try {

      let fileToUpload = selectedFile;

      // AUTO COMPRESS IF LARGE

      if (
        selectedFile.size > 1000000 ||
        selectedFile.type === 'image/png'
      ) {

        fileToUpload =
          await compressImage(selectedFile);
      }

      const formData = new FormData();

      formData.append(
        'file',
        fileToUpload
      );

      const response = await fetch(
        'https://guardiancloudcare.com/backend/api/scan_document.php',
        {
          method: 'POST',
          body: formData,
        }
      );

      const text = await response.text();

      console.log(text);

      let data;

      try {

        data = JSON.parse(text);

      } catch (e) {

        console.error(text);

        alert('Invalid server response');

        setScanning(false);

        return;
      }

      if (!data.success) {

        console.log(data);

        alert(
          JSON.stringify(
            data.ocr_response,
            null,
            2
          )
        );

        setScanning(false);

        return;
      }

      const extracted = data.data || {};

      setAiData(extracted);

      setScanResult({

        childName: `
          ${extracted.firstName || ''}
          ${extracted.middleName || ''}
          ${extracted.lastName || ''}
        `.trim(),

        age: extracted.age || '',

        incident:
          extracted.incidentType ||
          extracted.beneficiaryCategory ||
          '',

        date:
          extracted.birthDate ||
          '',

        location:
          extracted.address ||
          '',

        summary:
          extracted.reasonForAssistance ||
          extracted.otherRelevantInfo ||
          'No information detected',
      });

      setScanning(false);

    } catch (error) {

      console.error(error);

      alert('Failed to scan');

      setScanning(false);
    }
  };

  const applyToCase = () => {

    if (!aiData) return;

    localStorage.setItem(
      'ai_case_data',
      JSON.stringify(aiData)
    );

    window.location.href = '/case-intake';
  };

  return (
    <AppLayout
      title="AI Doc Scanner"
      breadcrumb={['Social Worker', 'AI Doc Scanner']}
    >

      <div className="space-y-4">

        {/* HEADER */}

        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-5 text-white flex items-center gap-4">

          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-brain-line text-2xl" />
          </div>

          <div>
            <h3 className="font-bold text-lg">
              AI Documentation Tool
            </h3>

            <p className="text-teal-100 text-sm">
              Upload documents for AI extraction and auto-fill case details
            </p>
          </div>

        </div>

        {/* UPLOAD SECTION */}

        <div className="bg-white rounded-xl border border-gray-100 p-6">

          <h3 className="font-semibold text-gray-800 mb-2">
            Upload Document for AI Extraction
          </h3>

          <p className="text-sm text-gray-500 mb-4">
            Upload SCSR, medical reports, or photos.
            AI will extract information and auto-fill case fields.
          </p>

          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-teal-400 transition-colors mb-4"
            onClick={() =>
              document.getElementById('scan-upload')?.click()
            }
          >

            <input
              id="scan-upload"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              hidden
              onChange={(e) => {

                if (e.target.files?.[0]) {

                  setSelectedFile(e.target.files[0]);

                  setScanFile(e.target.files[0].name);
                }
              }}
            />

            {scanFile ? (

              <div>

                <i className="ri-file-text-line text-3xl text-teal-500 mb-2 block" />

                <p className="text-sm font-medium text-gray-800">
                  {scanFile}
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  Click to change file
                </p>

              </div>

            ) : (

              <div>

                <i className="ri-upload-cloud-2-line text-3xl text-gray-300 mb-2 block" />

                <p className="text-sm text-gray-500">
                  Click to upload or drag & drop
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  PDF, DOCX, JPG, PNG up to 10MB
                </p>

              </div>

            )}

          </div>

          <button
            onClick={handleScan}
            disabled={!scanFile || scanning}
            className="bg-teal-500 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 flex items-center gap-2"
          >

            {scanning ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Scanning with AI...
              </>
            ) : (
              <>
                <i className="ri-brain-line" />
                Scan & Extract
              </>
            )}

          </button>

        </div>

        {/* RESULT */}

        {scanResult && (

          <div className="bg-white rounded-xl border border-teal-200 p-6">

            <div className="flex items-center gap-3 mb-4">

              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">

                <i className="ri-checkbox-circle-line text-teal-600 text-lg" />

              </div>

              <div>

                <p className="font-semibold text-gray-800">
                  AI Extraction Complete
                </p>

                <p className="text-xs text-gray-400">
                  Dynamic AI field extraction successful
                </p>

              </div>

            </div>

            {/* DYNAMIC FIELD DISPLAY */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

              {Object.entries(aiData || {}).map(([key, value]) => (

                <div key={key}>

                  <label className="text-xs font-medium text-gray-500 mb-1 block">

                    {key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (s) => s.toUpperCase())}

                  </label>

                  <input
                    value={String(value || '')}
                    readOnly
                    className="w-full border border-teal-200 rounded-xl px-3 py-2 text-sm bg-teal-50/30"
                  />

                </div>

              ))}

            </div>

            {/* SUMMARY */}

            <div className="mb-4">

              <label className="text-xs font-medium text-gray-500 mb-1 block">
                AI Summary
              </label>

              <textarea
                value={scanResult.summary}
                readOnly
                rows={4}
                className="w-full border border-teal-200 rounded-xl px-3 py-2 text-sm bg-teal-50/30 resize-none"
              />

            </div>

            {/* ACTIONS */}

            <div className="flex gap-3">

              <button
                onClick={applyToCase}
                className="bg-teal-500 text-white text-sm px-5 py-2 rounded-xl hover:bg-teal-600 flex items-center gap-2"
              >

                <i className="ri-save-line" />

                Apply to Case

              </button>

              <button
                onClick={() => {

                  setScanResult(null);

                  setAiData(null);

                  setSelectedFile(null);

                  setScanFile('');
                }}
                className="border border-gray-200 text-gray-700 text-sm px-4 py-2 rounded-xl hover:bg-gray-50"
              >

                Discard

              </button>

            </div>

          </div>

        )}

      </div>

    </AppLayout>
  );
}